import React from 'react';
import { cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { isExperimentEnabled } from '@confluence/feature-experiments';

const styles = cssMap({
	layout: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden',
		// Positioning context for the footer overlay below.
		position: 'relative',
	},
	labsLayout: {
		isolation: 'isolate',
		backgroundColor: token('elevation.surface'),
	},
	panelSystem: {
		// PanelBody provides a relative positioning context instead of a fixed height.
		position: 'absolute',
		inset: 0,
	},
	content: {
		flex: '1 1 0%',
		minHeight: '0px',
		overflow: 'auto',
		/**
		 * The footer floats over this area rather than taking a row of its own, so the space
		 * it needs is reserved here instead — visible only once the list is scrolled to the
		 * bottom. Reserve space for the action button, policy row, and breathing room.
		 *
		 * `paddingBlockEnd` lets the last card scroll clear of the footer; the matching
		 * `scrollPaddingBlockEnd` keeps anything brought in by `scrollIntoView` (a selected
		 * card, the inline loading bar) above the scrim rather than under it.
		 */
		paddingBlockEnd: `calc(${token('space.600')} + ${token('space.400')})`,
		scrollPaddingBlockEnd: `calc(${token('space.600')} + ${token('space.400')})`,
	},
	/**
	 * The footer floats over the scroll area rather than taking a row of its own, so the
	 * list runs the full height of the panel and scrolls under the scrim.
	 */
	footerOverlay: {
		position: 'absolute',
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
	},
});

type SuggestionsPanelLayoutProps = {
	content: React.ReactNode;
	footer: React.ReactNode;
};

/** Lays out the panel's scrolling content with its footer floating over the bottom. */
export const SuggestionsPanelLayout = ({
	content,
	footer,
}: SuggestionsPanelLayoutProps): React.JSX.Element => (
	<div
		css={[
			styles.layout,
			isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs') && styles.labsLayout,
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			fg('ads-panel-system-confluence-object-sidebar') && styles.panelSystem,
		]}
	>
		<div css={styles.content}>{content}</div>
		<div css={styles.footerOverlay}>{footer}</div>
	</div>
);
